// src/components/Platba.tsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatApproxEur } from '../utils/currency';
import { Reservation } from '../types';
import { updatePaymentMethod } from '../services/api';

const Platba: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [reservation, setReservation] = useState<Reservation | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    const load = async () => {
      const raw = localStorage.getItem('reservationData');
      if (!raw) {
        setError('Data rezervace nebyla nalezena. Prosím, vraťte se na stránku rezervace.');
        void navigate('/rezervace');
        return;
      }

      try {
        const reservationData = JSON.parse(raw) as Reservation;

        if (!reservationData.paymentMethod) {
          setError('Způsob platby není vybrán. Prosím, vraťte se na stránku rezervace.');
          void navigate('/rezervace');
          return;
        }

        if (!reservationData.isVerified) {
          setError('Pro pokračování prosím ověřte e-mail.');
          void navigate('/verify', { state: { reservation: reservationData, paymentMethod: reservationData.paymentMethod } });
          return;
        }

        setReservation(reservationData);

        // Automatické přesměrování podle způsobu platby
        if (reservationData.id) {
          setLoading(true);
          try {
            await updatePaymentMethod(reservationData.id, reservationData.paymentMethod);
          } catch (err) {
            if (import.meta.env.DEV) {
              // eslint-disable-next-line no-console
              console.error('Chyba při aktualizaci způsobu platby:', err);
            }
          }

          if (reservationData.paymentMethod === 'transfer') {
            void navigate('/transfer-payment', { state: { reservation: reservationData } });
            return;
          }
          if (reservationData.paymentMethod === 'cash') {
            void navigate('/cash-payment', { state: { reservation: reservationData } });
            return;
          }
        }
      } catch (err) {
        if (import.meta.env.DEV) {
          // eslint-disable-next-line no-console
          console.error('Chyba při parsování dat z localStorage nebo při aktualizaci způsobu platby:', err);
        }
        setError('Nepodařilo se načíst data rezervace nebo aktualizovat způsob platby. Prosím, vraťte se na stránku rezervace.');
        void navigate('/rezervace');
      } finally {
        setLoading(false);
      }
    };

    void load();
  }, [navigate, t]);

  if (error) {
    return <p className="text-red-500 text-center mt-10">{error}</p>;
  }
  if (!reservation || loading) {
    return <p className="text-center mt-10">{t('payment.loading')}</p>;
  }

  const inDt = new Date(reservation.checkInDate);
  const [hIn, mIn] = reservation.checkInTime.split(':').map(Number);
  inDt.setHours(hIn, mIn, 0, 0);

  const outDt = new Date(reservation.checkOutDate);
  const [hOut, mOut] = reservation.checkOutTime.split(':').map(Number);
  outDt.setHours(hOut, mOut, 0, 0);

  const msPerDay = 1000 * 60 * 60 * 24;
  const nights = Math.ceil((outDt.getTime() - inDt.getTime()) / msPerDay);

  return (
    <div className="max-w-xl mx-auto mt-10 p-6 bg-white rounded shadow space-y-4">
      <h2 className="text-2xl font-bold text-center">{t('payment.summary')}</h2>

      <div><strong>{t('payment.reservationNumber')}:</strong> {reservation.id}</div>
      <div><strong>{t('payment.stayTerm')}:</strong> {inDt.toLocaleDateString()} – {outDt.toLocaleDateString()}</div>
      <div><strong>{t('payment.nights')}:</strong> {nights}</div>

      <div><strong>{t('payment.persons')}:</strong> {reservation.adults} {t('payment.adults')}, {reservation.children} {t('payment.children')}</div>
      <div><strong>{t('payment.crib')}:</strong> {reservation.needsCrib ? t('payment.yes') : t('payment.no')}</div>
      <div><strong>{t('payment.extraBed')}:</strong> {reservation.needsExtraBed ? t('payment.yes') : t('payment.no')}</div>
      <div><strong>{t('payment.lateArrival')}:</strong> {reservation.checkInTime >= '21:00' ? t('payment.yes') : t('payment.no')}</div>

      {reservation.note && <div><strong>{t('payment.note')}:</strong> {reservation.note}</div>}

      <div className="bg-gray-100 p-4 rounded space-y-1">
        <div>
          <strong>{t('payment.totalPrice')}:</strong> {reservation.totalPrice?.toLocaleString()} Kč
          {i18n.language !== 'cs' && reservation.totalPrice && (
            <div className="text-sm text-gray-600 mt-1">(~ {formatApproxEur(reservation.totalPrice, i18n.language)})</div>
          )}
        </div>
      </div>

      <div><strong>{t('payment.name')}:</strong> {reservation.name}</div>
      <div><strong>{t('payment.email')}:</strong> {reservation.email}</div>
      <div><strong>{t('payment.phone')}:</strong> {reservation.phone}</div>

      <div><strong>{t('payment.verified')}:</strong> {reservation.isVerified ? t('payment.yes') : t('payment.no')}</div>
    </div>
  );
};

export default Platba;
